// Displaying amounts: decimals, fractions, units, simple plurals.
import type { Amount } from './quantity';
import type { ResolvedUnit } from './units';

/** Exact port of Swift IngredientQuantityFormatter.displayAmount (fixed-point → text). */
export function displayFixedPoint(a: Amount): string {
  if (a.places <= 0) return String(a.value);
  const sign = a.value < 0 ? '-' : '';
  const abs = Math.abs(a.value);
  const divisor = 10 ** a.places;
  const whole = Math.floor(abs / divisor);
  const fraction = String(abs % divisor).padStart(a.places, '0').replace(/0+$/, '');
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
}

/** Decimal text, at most 3 places (the precision amounts are stored with), no trailing zeros: 1.50 → "1.5". */
export function formatDecimal(n: number, maxPlaces = 3, decimalSeparator = '.'): string {
  const factor = 10 ** maxPlaces;
  const rounded = Math.round(n * factor) / factor;
  let text = rounded.toFixed(maxPlaces);
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  if (text === '-0') text = '0';
  return decimalSeparator === '.' ? text : text.replace('.', decimalSeparator);
}

const FRACTIONS: [number, string][] = [
  [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [1 / 2, '½'], [2 / 3, '⅔'], [3 / 4, '¾'],
];

/**
 * Snaps to a kitchen fraction when within 2% of the value ("1 ½", "¾"); otherwise decimals.
 * Whole numbers stay whole ("2").
 */
export function formatFraction(n: number, decimalSeparator = '.'): string {
  if (n <= 0) return formatDecimal(n, 3, decimalSeparator);
  const tolerance = Math.max(n * 0.02, 1e-9);
  const whole = Math.floor(n);
  const rest = n - whole;
  if (rest <= tolerance) return String(whole);
  if (1 - rest <= tolerance) return String(whole + 1);
  for (const [value, glyph] of FRACTIONS) {
    if (Math.abs(rest - value) <= tolerance) return whole > 0 ? `${whole} ${glyph}` : glyph;
  }
  return formatDecimal(n, 3, decimalSeparator);
}

/**
 * Amount text depends on the unit, not the user's system:
 * metric units → decimals ("1.5 kg"); US/imperial units, custom units ("pinch") and plain
 * counts → kitchen fractions ("1 ½ cups", "½ onion").
 */
export function formatQuantity(n: number, unit: ResolvedUnit | null, decimalSeparator = '.'): string {
  return unit && isMetric(unit) ? formatDecimal(n, 3, decimalSeparator) : formatFraction(n, decimalSeparator);
}

const METRIC_KEYS = new Set(['milligram', 'gram', 'kilogram', 'milliliter', 'centiliter', 'deciliter', 'liter', 'metricTeaspoon', 'metricTablespoon']);
const METRIC_SYMBOLS = new Set(['mg', 'g', 'kg', 'ml', 'cl', 'dl', 'l']);
function isMetric(unit: ResolvedUnit): boolean {
  return unit.seed ? METRIC_KEYS.has(unit.seed) : METRIC_SYMBOLS.has(unit.symbol.toLowerCase());
}

/**
 * Simple English plural for grocery rows without a unit ("3 Lemons", "3 Tomatoes").
 * Swift added a bare "s" in every language ("Tomatos", German "Eis"); this is English-only
 * and handles -o/-x/-ch/-sh/-y endings.
 */
export function pluralTitle(title: string, amount: number, hasUnit: boolean, language: string): string {
  const lower = title.toLowerCase();
  if (!language.startsWith('en') || hasUnit || amount === 1 || lower.endsWith('s')) return title;
  if (/(o|x|ch|sh)$/.test(lower)) return `${title}es`;
  if (/[^aeiou]y$/.test(lower)) return `${title.slice(0, -1)}ies`;
  return `${title}s`;
}

/** "200 g of Flour" / "3 of Lemon" — Swift ingredientRowText wording; `of` is localized by the caller. */
export function ingredientLine(amountText: string, unitSymbol: string | null, title: string, of = 'of'): string {
  return [amountText, unitSymbol, of, title].filter(Boolean).join(' ');
}

/** 62 -> "1:02", 3725 -> "1:02:05": a live countdown. */
export function formatCountdown(totalSeconds: number): string {
  const t = Math.max(0, Math.ceil(totalSeconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
